import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import AppLayout from '../components/layout/AppLayout';
import { useTimezone, formatTimeInTimezone, PAGE, SHIFT, shiftStatus } from '@kioskgaming/ui';
import { fetchCashierExceptions, fetchShopCashiers, fetchShopShifts } from '../services/api';
import type { CashierShift, ShopCashier } from '../types';

type ViewMode = 'calendar' | 'list';

function startOfWeek(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

function addDays(date: Date, days: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function toDateInputValue(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}


function formatDayLabel(date: Date): string {
  return date.toLocaleDateString([], { weekday: 'short', day: 'numeric' });
}

function shiftStatusClass(status: string): string {
  if (status === 'active') return 'border-emerald-200 bg-emerald-50 text-emerald-800';
  if (status === 'scheduled') return 'border-blue-200 bg-blue-50 text-blue-800';
  if (status === 'no_show') return 'border-rose-200 bg-rose-50 text-rose-800';
  if (status === 'pending_manager_review' || status === 'pending_reconciliation') {
    return 'border-amber-200 bg-amber-50 text-amber-800';
  }
  if (status === 'completed' || status === 'completed_with_discrepancy') {
    return 'border-stone-200 bg-stone-50 text-stone-700';
  }
  return 'border-stone-200 bg-white text-stone-700';
}

function shiftDayKey(shift: CashierShift): string | null {
  if (shift.shiftDate) return shift.shiftDate.slice(0, 10);
  if (shift.scheduledStartAt) return shift.scheduledStartAt.slice(0, 10);
  return null;
}

export default function ShiftsPage() {
  const { timezone } = useTimezone();
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<ViewMode>('calendar');
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [shifts, setShifts] = useState<CashierShift[]>([]);
  const [cashiers, setCashiers] = useState<ShopCashier[]>([]);
  const [exceptionCount, setExceptionCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const weekDays = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart]
  );

  const weekEnd = useMemo(() => addDays(weekStart, 6), [weekStart]);

  const cashierNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of cashiers) map.set(c.id, c.name);
    return map;
  }, [cashiers]);

  const getApiErrorMessage = (e: unknown) => {
    const anyE = e as { response?: { data?: { message?: string } }; message?: string };
    return anyE?.response?.data?.message || anyE?.message || null;
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const from = toDateInputValue(weekStart);
      const to = toDateInputValue(addDays(weekEnd, 1));
      const [shiftRes, cashierRes, exceptionRes] = await Promise.all([
        fetchShopShifts({ from, to }),
        fetchShopCashiers(),
        fetchCashierExceptions(),
      ]);
      if (!shiftRes.success) throw new Error(shiftRes.message || 'Failed to load shifts');
      setShifts(shiftRes.data || []);
      if (cashierRes.success) setCashiers(cashierRes.data || []);
      if (exceptionRes.success) setExceptionCount((exceptionRes.data || []).length);
    } catch (e: unknown) {
      toast.error(getApiErrorMessage(e) || 'Failed to load shifts');
    } finally {
      setLoading(false);
    }
  }, [weekStart, weekEnd]);

  useEffect(() => {
    void load();
  }, [load]);

  const todayKey = toDateInputValue(new Date());

  const metrics = useMemo(() => {
    const todayShifts = shifts.filter((s) => shiftDayKey(s) === todayKey);
    const active = todayShifts.filter((s) => s.status === 'active').length;
    const noShow = todayShifts.filter((s) => s.status === 'no_show').length;
    const scheduled = todayShifts.filter((s) => s.status === 'scheduled').length;
    return { todayTotal: todayShifts.length, active, noShow, scheduled };
  }, [shifts, todayKey]);

  const shiftsByDay = useMemo(() => {
    const map = new Map<string, CashierShift[]>();
    for (const day of weekDays) {
      map.set(toDateInputValue(day), []);
    }
    for (const shift of shifts) {
      const key = shiftDayKey(shift);
      if (key && map.has(key)) {
        map.get(key)!.push(shift);
      }
    }
    Array.from(map.values()).forEach((list) => {
      list.sort((a, b) => {
        const ta = a.scheduledStartAt || a.openedAt || '';
        const tb = b.scheduledStartAt || b.openedAt || '';
        return ta.localeCompare(tb);
      });
    });
    return map;
  }, [shifts, weekDays]);

  const weekLabel = `${weekStart.toLocaleDateString([], { month: 'short', day: 'numeric' })} – ${weekEnd.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })}`;

  return (
    <AppLayout contentFullWidth>
      <div className="w-full max-w-none">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold">{PAGE.shifts}</h1>
            <p className="mt-0.5 text-sm text-stone-600">
              Create shifts in advance and prevent Cashier or device conflicts.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              className={`rounded-lg border px-3 py-2 text-sm font-semibold ${
                viewMode === 'list'
                  ? 'border-emerald-600 bg-emerald-50 text-emerald-700'
                  : 'border-stone-200 text-stone-700 hover:bg-stone-50'
              }`}
              onClick={() => setViewMode('list')}
            >
              List view
            </button>
            <button
              type="button"
              className={`rounded-lg border px-3 py-2 text-sm font-semibold ${
                viewMode === 'calendar'
                  ? 'border-emerald-600 bg-emerald-50 text-emerald-700'
                  : 'border-stone-200 text-stone-700 hover:bg-stone-50'
              }`}
              onClick={() => setViewMode('calendar')}
            >
              Calendar
            </button>
            <button
              type="button"
              className="rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
              onClick={() => navigate('/shifts/new')}
            >
              + {SHIFT.scheduleShift}
            </button>
          </div>
        </div>

        <div className="mt-4 grid gap-3 sm:grid-cols-3">
          <div className="rounded-xl border border-stone-200 bg-white p-4">
            <div className="text-xs font-medium uppercase tracking-wide text-stone-500">Today</div>
            <div className="mt-1 text-2xl font-bold text-stone-900">{metrics.todayTotal} shifts</div>
            <div className="mt-0.5 text-xs text-stone-600">
              {metrics.scheduled} scheduled · {metrics.noShow} no-show
            </div>
          </div>
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
            <div className="text-xs font-medium uppercase tracking-wide text-emerald-700">Active now</div>
            <div className="mt-1 text-2xl font-bold text-emerald-900">{metrics.active}</div>
            <div className="mt-0.5 text-xs text-emerald-700">Currently open shifts</div>
          </div>
          <button
            type="button"
            className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-left hover:bg-amber-100"
            onClick={() => navigate('/cashier-exceptions')}
          >
            <div className="text-xs font-medium uppercase tracking-wide text-amber-700">Needs attention</div>
            <div className="mt-1 text-2xl font-bold text-amber-900">{exceptionCount || metrics.noShow}</div>
            <div className="mt-0.5 text-xs text-amber-700">
              {exceptionCount
                ? `${exceptionCount} exception${exceptionCount === 1 ? '' : 's'} (auto-lock, no-show, review)`
                : 'No-show shifts today'}
            </div>
          </button>
        </div>

        <div className="mt-4 rounded-xl border border-stone-200 bg-white">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-stone-200 px-4 py-3">
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="rounded-lg border border-stone-200 px-3 py-1.5 text-xs font-semibold hover:bg-stone-50"
                onClick={() => setWeekStart((d) => addDays(d, -7))}
              >
                ‹ Previous
              </button>
              <button
                type="button"
                className="rounded-lg border border-stone-200 px-3 py-1.5 text-xs font-semibold hover:bg-stone-50"
                onClick={() => setWeekStart(startOfWeek(new Date()))}
              >
                Today
              </button>
              <button
                type="button"
                className="rounded-lg border border-stone-200 px-3 py-1.5 text-xs font-semibold hover:bg-stone-50"
                onClick={() => setWeekStart((d) => addDays(d, 7))}
              >
                Next ›
              </button>
              <span className="text-sm font-medium text-stone-700">{weekLabel}</span>
            </div>
            <div className="flex flex-wrap items-center gap-3 text-xs text-stone-600">
              <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-emerald-500" /> Active</span>
              <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-blue-500" /> Scheduled</span>
              <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-rose-500" /> No-show</span>
              <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-amber-500" /> Needs review</span>
              <button
                type="button"
                className="rounded-lg border border-stone-200 px-3 py-1.5 text-xs font-semibold hover:bg-stone-50"
                onClick={() => void load()}
              >
                Refresh
              </button>
            </div>
          </div>

          {loading ? (
            <div className="px-4 py-8 text-sm text-stone-600">Loading…</div>
          ) : viewMode === 'calendar' ? (
            <div className="overflow-x-auto">
              <div className="grid min-w-[720px] grid-cols-8 border-b border-stone-100">
                <div className="border-r border-stone-100 bg-stone-50 px-3 py-2 text-xs font-semibold uppercase text-stone-500">
                  Day
                </div>
                {weekDays.map((day) => {
                  const key = toDateInputValue(day);
                  const isToday = key === todayKey;
                  return (
                    <div
                      key={key}
                      className={`border-r border-stone-100 px-3 py-2 text-center text-xs font-semibold ${
                        isToday ? 'bg-emerald-50 text-emerald-800' : 'bg-stone-50 text-stone-600'
                      }`}
                    >
                      {formatDayLabel(day)}
                    </div>
                  );
                })}
              </div>
              <div className="grid min-w-[720px] grid-cols-8">
                <div className="border-r border-stone-100 px-3 py-3 text-xs text-stone-500">All day</div>
                {weekDays.map((day) => {
                  const key = toDateInputValue(day);
                  const dayShifts = shiftsByDay.get(key) || [];
                  return (
                    <div key={key} className="min-h-[120px] border-r border-b border-stone-100 p-2">
                      {dayShifts.map((shift) => (
                        <button
                          key={shift.id}
                          type="button"
                          onClick={() => navigate(`/shifts/${encodeURIComponent(shift.id)}`)}
                          className={`mb-2 w-full rounded-md border px-2 py-1.5 text-left text-[11px] ${shiftStatusClass(shift.status)}`}
                        >
                          <div className="font-semibold">
                            {formatTimeInTimezone(shift.scheduledStartAt, timezone)}–{formatTimeInTimezone(shift.scheduledEndAt, timezone)}
                          </div>
                          <div className="mt-0.5 truncate font-medium">
                            {cashierNameById.get(shift.cashierId) || 'Cashier'}
                          </div>
                          <div className="truncate opacity-80">{shiftStatus(shift.status).label}</div>
                        </button>
                      ))}
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="max-h-[70vh] overflow-y-auto">
              {shifts.length ? (
                shifts.map((shift) => (
                  <button
                    key={shift.id}
                    type="button"
                    onClick={() => navigate(`/shifts/${encodeURIComponent(shift.id)}`)}
                    className="flex w-full items-center justify-between gap-3 border-b border-stone-100 px-4 py-3 text-left hover:bg-stone-50"
                  >
                    <div className="min-w-0">
                      <div className="truncate text-sm font-semibold text-stone-900">
                        {cashierNameById.get(shift.cashierId) || 'Cashier'}
                      </div>
                      <div className="mt-0.5 text-xs text-stone-500">
                        {shift.shiftDate || shift.scheduledStartAt?.slice(0, 10) || '—'} ·{' '}
                        {formatTimeInTimezone(shift.scheduledStartAt, timezone)}–{formatTimeInTimezone(shift.scheduledEndAt, timezone)}
                      </div>
                    </div>
                    <span
                      className={`shrink-0 rounded-full px-2 py-1 text-[11px] font-semibold ${shiftStatusClass(shift.status)}`}
                    >
                      {shiftStatus(shift.status).label}
                    </span>
                  </button>
                ))
              ) : (
                <div className="px-4 py-6 text-sm text-stone-600">No shifts in this week.</div>
              )}
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
